import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { searchApi } from "lib/api";
import type { WikiSuggestion } from "util/constants";
import { getWikipediaSummary } from "util/wikiUtils";
import type {
  SearchResponse,
  SearchFilters,
  SearchScope,
  SearchSuggestionsResponse,
  TrendingSearchResponse,
} from "types/search";

export const searchQueryKeys = {
  results: (query: string, scope: SearchScope, limit: number, filters: SearchFilters = {}) =>
    ["search", "results", query, scope, limit, filters] as const,
  suggestions: (query: string, limit: number, filters: SearchFilters = {}) =>
    ["search", "suggestions", query, limit, filters] as const,
  wikipediaSuggestions: (query: string, limit: number, existingIssueKey: string) =>
    ["search", "wikipedia-suggestions", query, limit, existingIssueKey] as const,
  trending: (limit: number) => ["search", "trending", limit] as const,
};

const normalizeIssueKey = (value: string): string => value.trim().toLowerCase();

interface WikipediaOpenSearchApiResponse extends Array<unknown> {
  1?: string[];
}

interface WikipediaIssueSuggestionsPageParam {
  excludeCanonicalTitles: string[];
  limit: number;
}

interface WikipediaIssueSuggestionsPage {
  items: WikiSuggestion[];
  nextPageParam: WikipediaIssueSuggestionsPageParam | null;
}

export const useGlobalSearchQuery = (
  query: string,
  scope: SearchScope,
  limit = 20,
  enabled = true,
  filters: SearchFilters = {}
) =>
  useInfiniteQuery<SearchResponse>({
    queryKey: searchQueryKeys.results(query, scope, limit, filters),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      searchApi.search({
        query,
        scope,
        ...filters,
        limit,
        offset: typeof pageParam === "number" ? pageParam : 0,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore
        ? lastPage.pagination.offset + lastPage.pagination.limit
        : undefined,
    enabled: enabled && query.trim().length >= 2,
  });

export const useGlobalSearchSuggestionsQuery = (
  query: string,
  limit = 5,
  enabled = true,
  filters: SearchFilters = {}
) =>
  useQuery<SearchSuggestionsResponse>({
    queryKey: searchQueryKeys.suggestions(query, limit, filters),
    queryFn: () =>
      searchApi.getSuggestions({
        query,
        ...filters,
        limit,
      }),
    enabled: enabled && query.trim().length >= 2,
    staleTime: 30_000,
  });

export const useGlobalSearchTrendingQuery = (limit = 6, enabled = true) =>
  useQuery<TrendingSearchResponse>({
    queryKey: searchQueryKeys.trending(limit),
    queryFn: () =>
      searchApi.getTrending({
        limit,
      }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

const fetchWikipediaIssueSuggestionsPage = async (
  query: string,
  pageParam: WikipediaIssueSuggestionsPageParam,
  pageSize: number
): Promise<WikipediaIssueSuggestionsPage> => {
  const suggestions: WikiSuggestion[] = [];
  const seenCanonicalTitles = new Set(pageParam.excludeCanonicalTitles.map(normalizeIssueKey));

  const wikiResponse = await fetch(
    `https://en.wikipedia.org/w/api.php?action=opensearch&search=${encodeURIComponent(
      query
    )}&limit=${pageParam.limit}&namespace=0&format=json&origin=*`
  );

  if (!wikiResponse.ok) {
    throw new Error(`Wikipedia open search failed: ${wikiResponse.status}`);
  }

  const data = (await wikiResponse.json()) as WikipediaOpenSearchApiResponse;
  const rawTitles = Array.isArray(data[1])
    ? data[1].filter((title): title is string => typeof title === "string" && title.trim().length > 0)
    : [];

  if (rawTitles.length === 0) {
    return {
      items: [],
      nextPageParam: null,
    };
  }

  const unseenTitles = rawTitles.filter((title) => !seenCanonicalTitles.has(normalizeIssueKey(title)));

  const summaries = await Promise.all(
    unseenTitles.map(async (title) => {
      const summary = await getWikipediaSummary(title);

      if (!summary) {
        return null;
      }

      const canonicalKey = normalizeIssueKey(summary.canonicalTitle);

      if (seenCanonicalTitles.has(canonicalKey)) {
        return null;
      }

      seenCanonicalTitles.add(canonicalKey);

      return {
        type: "wiki" as const,
        name: summary.title,
        link: summary.wikipediaUrl,
        thumbnail: summary.thumbnail || "",
        cname: summary.canonicalTitle,
        short_description: summary.description || undefined,
        subject_properties: summary,
      };
    })
  );

  summaries.forEach((summary) => {
    if (summary) {
      suggestions.push(summary);
    }
  });

  const nextPageParam =
    rawTitles.length >= pageParam.limit && pageParam.limit < 500
      ? {
          limit: pageParam.limit + pageSize,
          excludeCanonicalTitles: Array.from(seenCanonicalTitles),
        }
      : null;

  return {
    items: suggestions,
    nextPageParam,
  };
};

export const useWikipediaIssueSuggestionsInfiniteQuery = (
  query: string,
  existingIssueCnames: string[],
  limit = 5,
  enabled = true
) =>
  useInfiniteQuery<WikipediaIssueSuggestionsPage>({
    queryKey: searchQueryKeys.wikipediaSuggestions(
      query,
      limit,
      existingIssueCnames.map(normalizeIssueKey).sort().join("|")
    ),
    initialPageParam: {
      limit,
      excludeCanonicalTitles: existingIssueCnames.map(normalizeIssueKey),
    },
    queryFn: ({ pageParam }) =>
      fetchWikipediaIssueSuggestionsPage(
        query,
        pageParam as WikipediaIssueSuggestionsPageParam,
        limit
      ),
    getNextPageParam: (lastPage) => lastPage.nextPageParam ?? undefined,
    enabled: enabled && query.trim().length >= 2,
    staleTime: 30_000,
    retry: false,
  });
